import type {
  DeploymentProfileApplication,
  Application,
  AppVersion,
} from '@interlucent/organization-protocol';
import type { MappingWithApp } from '../types/mapping.types';

/**
 * Mapping business logic
 * Pure functions for mapping operations
 */

export const mappingService = {
  /**
   * Get all applications mapped to a profile
   */
  getMappedApplications: (
    profileId: string,
    mappings: Record<string, DeploymentProfileApplication>,
    applications: Record<string, Application>
  ): Application[] => {
    return Object.values(mappings)
      .filter(m => m.deploymentProfileId === profileId)
      .map(m => applications[m.appId])
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * Get available applications for mapping (not already mapped)
   */
  getAvailableApplications: (
    profileId: string,
    projectId: string,
    mappings: Record<string, DeploymentProfileApplication>,
    applications: Record<string, Application>
  ): Application[] => {
    const mappedAppIds = new Set(
      Object.values(mappings)
        .filter(m => m.deploymentProfileId === profileId)
        .map(m => m.appId)
    );

    return Object.values(applications)
      .filter(app => app.projectId === projectId && !mappedAppIds.has(app.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * Check if application is already mapped to profile
   */
  isMapped: (
    profileId: string,
    appId: string,
    mappings: Record<string, DeploymentProfileApplication>
  ): boolean => {
    return Object.values(mappings).some(
      m => m.deploymentProfileId === profileId && m.appId === appId
    );
  },

  /**
   * Get mapping by profile and app ID
   */
  getMapping: (
    profileId: string,
    appId: string,
    mappings: Record<string, DeploymentProfileApplication>
  ): DeploymentProfileApplication | undefined => {
    return Object.values(mappings).find(
      m => m.deploymentProfileId === profileId && m.appId === appId
    );
  },

  /**
   * Get all mappings for a profile
   */
  getProfileMappings: (
    profileId: string,
    mappings: Record<string, DeploymentProfileApplication>
  ): DeploymentProfileApplication[] => {
    return Object.values(mappings)
      .filter(m => m.deploymentProfileId === profileId)
      .sort((a, b) => {
        const aDate = new Date(a.createdAt || 0);
        const bDate = new Date(b.createdAt || 0);
        return bDate.getTime() - aDate.getTime(); // Most recent first
      });
  },

  /**
   * Get mappings with application details
   */
  getMappingsWithApps: (
    profileId: string,
    mappings: Record<string, DeploymentProfileApplication>,
    applications: Record<string, Application>
  ): MappingWithApp[] => {
    return mappingService
      .getProfileMappings(profileId, mappings)
      .map(mapping => ({
        ...mapping,
        application: applications[mapping.appId]
      }))
      .filter(m => m.application); // Filter out mappings with missing apps
  },

  /**
   * Get mapping count for a profile
   */
  getMappingCount: (
    profileId: string,
    mappings: Record<string, DeploymentProfileApplication>
  ): number => {
    return Object.values(mappings).filter(
      m => m.deploymentProfileId === profileId
    ).length;
  },

  /**
   * Get all profiles an application is mapped to
   */
  getApplicationProfiles: (
    appId: string,
    mappings: Record<string, DeploymentProfileApplication>
  ): string[] => {
    return Object.values(mappings)
      .filter(m => m.appId === appId)
      .map(m => m.deploymentProfileId);
  },

  /**
   * Validate if an application can be mapped to a profile
   */
  canMapApplication: (
    profileId: string,
    appId: string,
    mappings: Record<string, DeploymentProfileApplication>,
    applications: Record<string, Application>
  ): { can: boolean; reason?: string } => {
    const app = applications[appId];
    if (!app) {
      return { can: false, reason: "Application not found" };
    }

    if (mappingService.isMapped(profileId, appId, mappings)) {
      return { can: false, reason: "Application is already mapped to this profile" };
    }

    return { can: true };
  }
};
